import React, { useState, useEffect } from "react";
import { Menu, X, Phone, MapPin } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { cn } from "@/lib/utils";
function HeaderLogo() {
  return <Link to="/HomePage" className="flex items-center gap-2 z-50 relative">
      <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1505473/img/akk_monogram_logo.png" alt="Ubud Event Space" className="h-7 w-auto object-contain" />
    </Link>;
}
function DesktopNav() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="10629" className="hidden lg:flex items-center gap-6">
        {[1, 2, 3, 4].map(i => <div key={i} data-index={i} className="h-4 w-20 bg-muted animate-pulse rounded" />)}
      </div>;
  }
  return <NavigationMenu className="hidden lg:flex">
      <NavigationMenuList className="gap-1">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className={cn("group relative px-4 py-2 text-sm font-sans font-medium text-foreground transition-colors hover:text-secondary", "after:absolute after:bottom-0 after:left-0 after:h-[2px] after:w-full after:origin-bottom-right after:scale-x-0 after:bg-secondary after:transition-transform after:duration-300 hover:after:origin-bottom-left hover:after:scale-x-100")}>
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileNav({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const {
    menuItems
  } = useMenu();
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);
  return <div className={cn("fixed inset-0 z-40 bg-background/95 backdrop-blur-sm transition-all duration-300 lg:hidden flex flex-col", isOpen ? "opacity-100 visible" : "opacity-0 invisible pointer-events-none")}>
      <div className="container mx-auto px-4 py-24 flex flex-col gap-8 h-full overflow-y-auto">
        <nav className="flex flex-col gap-6 items-center text-center">
          {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="text-2xl font-serif font-medium text-foreground hover:text-secondary transition-colors" onClick={onClose}>
              {item.label}
            </Link>)}
        </nav>

        <div className="mt-auto flex flex-col gap-6 items-center pb-8">
          <Button size="lg" className="w-full max-w-xs bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/20" asChild>
            <Link to="/ConnectPage" onClick={onClose}>
              Book via WhatsApp
            </Link>
          </Button>

          <div className="flex flex-col gap-4 text-sm text-muted-foreground text-center">
            <div className="flex items-center justify-center gap-2">
              <Phone className="h-4 w-4 text-secondary" />
              <span>+62 812 3456 7890</span>
            </div>
            <div className="flex items-center justify-center gap-2">
              <MapPin className="h-4 w-4 text-secondary" />
              <span>Peliatan, Ubud, Bali</span>
            </div>
          </div>
        </div>
      </div>
    </div>;
}
function HeaderContent() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <>
      <header className={cn("sticky top-0 z-50 w-full border-b border-transparent transition-all duration-500 ease-in-out", isScrolled ? "bg-background/80 backdrop-blur-md border-border/40 shadow-sm py-2" : "bg-background py-4")}>
        <div className="container mx-auto px-4 flex items-center justify-between">
          {}
          <HeaderLogo />

          {}
          <DesktopNav />

          {}
          <div className="hidden lg:flex items-center gap-4">
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-md shadow-primary/10 font-sans font-medium px-6" asChild>
              <Link to="/ConnectPage">Book via WhatsApp</Link>
            </Button>
          </div>

          {}
          <Button variant="ghost" size="icon" className="lg:hidden z-50 relative hover:bg-transparent hover:text-secondary" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </div>
      </header>

      {}
      <MobileNav isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />
    </>;
}
export default function Header() {
  return <section id="header" className="relative w-full bg-background text-foreground">
      <MenuProvider menu_id="79" className="w-full">
        <HeaderContent />
      </MenuProvider>
    </section>;
}